import type { ApplicationFormFieldData } from '@/schemas/v2'
import type { ChangeEvent } from 'react'

type Props = {
  field: ApplicationFormFieldData
  value: string | string[]
  onChange: (fieldKey: string, value: string | string[]) => void
  onBlur?: (fieldKey: string) => void
  disabled?: boolean
  /** クライアントサイドバリデーションエラーメッセージ（onBlur 後に表示） */
  clientError?: string
  /** サーバーサイドバリデーションエラーメッセージ（clientError より優先して表示） */
  serverError?: string
}

export function ApplicationFormField({
  field,
  value,
  onChange,
  onBlur,
  disabled = false,
  clientError,
  serverError,
}: Props) {
  // serverError 優先、なければ clientError
  const displayError = serverError ?? clientError

  const baseInputClass =
    'w-full rounded-md border px-3 py-2 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500'

  const inputClass = `${baseInputClass} ${displayError ? 'border-red-500' : 'border-gray-300'}`

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange(field.fieldKey, e.target.value)
  }

  const handleBlur = () => {
    onBlur?.(field.fieldKey)
  }

  const handleCheckboxChange = (option: string, checked: boolean) => {
    const current = Array.isArray(value) ? value : []
    const next = checked ? [...current, option] : current.filter((v) => v !== option)
    onChange(field.fieldKey, next)
  }

  return (
    <div className="flex flex-col gap-1">
      {field.inputType === 'checkbox' ? (
        // checkboxグループはfieldset/legendで構造化するため、外側のlabelは不要
        <span className="text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="ml-1 text-red-500">*</span>}
        </span>
      ) : (
        <label htmlFor={field.fieldKey} className="text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {field.inputType === 'textarea' && (
        <textarea
          id={field.fieldKey}
          value={typeof value === 'string' ? value : ''}
          onChange={handleChange}
          onBlur={handleBlur}
          required={field.isRequired}
          disabled={disabled}
          rows={4}
          aria-invalid={displayError !== undefined ? true : undefined}
          aria-describedby={displayError ? `${field.fieldKey}-error` : undefined}
          className={inputClass}
        />
      )}

      {field.inputType === 'select' && (
        <select
          id={field.fieldKey}
          value={typeof value === 'string' ? value : ''}
          onChange={handleChange}
          onBlur={handleBlur}
          required={field.isRequired}
          disabled={disabled}
          aria-invalid={displayError !== undefined ? true : undefined}
          aria-describedby={displayError ? `${field.fieldKey}-error` : undefined}
          className={inputClass}
        >
          <option value="">選択してください</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}

      {field.inputType === 'checkbox' && (
        <fieldset className="flex flex-col gap-2">
          <legend className="sr-only">{field.label}</legend>
          {(field.options ?? []).map((opt, idx) => {
            const optId = `${field.fieldKey}-${idx}`
            return (
              <label key={opt} htmlFor={optId} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  id={optId}
                  type="checkbox"
                  value={opt}
                  checked={Array.isArray(value) ? value.includes(opt) : false}
                  onChange={(e) => handleCheckboxChange(opt, e.target.checked)}
                  onBlur={handleBlur}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {opt}
              </label>
            )
          })}
        </fieldset>
      )}

      {/* type="text" に統一してブラウザネイティブバリデーション吹き出しを抑制 */}
      {(field.inputType === 'text' || field.inputType === 'email' || field.inputType === 'tel') && (
        <input
          id={field.fieldKey}
          type="text"
          inputMode={field.inputType === 'tel' ? 'tel' : field.inputType === 'email' ? 'email' : 'text'}
          autoComplete={field.inputType === 'email' ? 'email' : field.inputType === 'tel' ? 'tel' : undefined}
          value={typeof value === 'string' ? value : ''}
          onChange={handleChange}
          onBlur={handleBlur}
          required={field.isRequired}
          disabled={disabled}
          aria-invalid={displayError !== undefined ? true : undefined}
          aria-describedby={displayError ? `${field.fieldKey}-error` : undefined}
          className={inputClass}
        />
      )}

      {displayError && (
        <p id={`${field.fieldKey}-error`} className="text-xs text-red-500" role="alert">
          {displayError}
        </p>
      )}
    </div>
  )
}
